import React, { useState, useEffect, useCallback } from 'react';
import {
  Scale, Clock, CheckCircle, XCircle, AlertCircle,
  Search, RefreshCw,
  SkipForward, Calendar, MapPin,
  Paperclip, Trash2, Download, FileCheck2,
} from 'lucide-react';
import { validate } from '../../validation/engine';
import Toast, { ToastMessage } from '../common/Toast';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Skeleton from '../common/Skeleton';
import NoDataFound from '../common/NoDataFound';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import statutoryService, {
  type StatInstance,
  type StatInstanceDetail,
  type StatInstanceStep,
  type StatStepEvidence,
  ROLE_LABEL,
} from '../../services/statutoryService';
import { visibleForScope, isClosedInstance } from '../../views/member/work/workArchive';

// ── Status icon map ───────────────────────────────────────────────────────────

const STATUS_ICON: Record<string, React.ReactNode> = {
  PENDING:     <Clock size={12} className="text-gray-400" />,
  IN_PROGRESS: <AlertCircle size={12} className="text-blue-500" />,
  COMPLETED:   <CheckCircle size={12} className="text-green-500" />,
  CANCELLED:   <XCircle size={12} className="text-red-400" />,
};

// Map an obligation / step status onto a shared `status-badge` semantic variant
// (success / info / warning / danger / neutral) so badges look identical to the
// rest of the app instead of hand-rolled Tailwind colour chains.
const STATUS_VARIANT: Record<string, string> = {
  COMPLETED:   'success',
  IN_PROGRESS: 'info',
  ACTIVE:      'info',
  PENDING:     'warning',
  SKIPPED:     'neutral',
  CANCELLED:   'danger',
};
const statusVariant = (s: string): string => STATUS_VARIANT[s] ?? 'neutral';

// The maker checklist shows only the maker (Perform) steps. Checker steps
// (Approve / Review) are not the maker's to action, so they are hidden from
// this table rather than shown as a non-actionable duplicate row.
const isMakerStep = (step: StatInstanceStep): boolean =>
  step.role !== 'APPROVE' && step.role !== 'REVIEW';

// ── Proof of compliance panel ─────────────────────────────────────────────────
// Used inside the Mark Complete popup (upload/remove) and read-only on a step
// that is already signed off. The file goes to the shared UTL_files store, so
// statutory proof lives in the same place as audit evidence.
const ProofPanel: React.FC<{
  stepId: number;
  evidence: StatStepEvidence[];
  readOnly?: boolean;
  onChanged: () => Promise<void> | void;
  onError: (msg: string) => void;
}> = ({ stepId, evidence, readOnly = false, onChanged, onError }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await statutoryService.uploadProof(stepId, file);
      await onChanged();
    } catch (err: any) {
      onError(err?.response?.data?.detail || err?.message || 'Upload failed.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (evId: number) => {
    setBusy(true);
    try {
      await statutoryService.deleteEvidence(stepId, evId);
      await onChanged();
    } catch (err: any) {
      onError(err?.response?.data?.detail || 'Could not remove the file.');
    } finally { setBusy(false); }
  };

  const download = async (ev: StatStepEvidence) => {
    try { await statutoryService.downloadEvidence(stepId, ev.id, ev.file_name); }
    catch { onError('Could not download the file.'); }
  };

  return (
    <div className="stat-proof">
      <div className="stat-proof__head">
        <span className="stat-proof__label">
          <FileCheck2 size={12} /> Proof of compliance
          {!readOnly && <span className="stat-proof__req">required</span>}
        </span>
        {!readOnly && (
          <>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              data-testid={`INP_statutory_step_${stepId}_proof_file`}
              onChange={pick}
            />
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              data-testid={`BTN_statutory_step_${stepId}_attach`}
            >
              <Paperclip size={13} className="mr-1" /> {busy ? 'Uploading…' : 'Attach file'}
            </Button>
          </>
        )}
      </div>

      {evidence.length === 0 ? (
        <p className="stat-proof__empty">
          {readOnly
            ? 'No proof was attached.'
            : 'Attach the filed return, challan or acknowledgement.'}
        </p>
      ) : (
        <ul className="stat-proof__list">
          {evidence.map((ev) => (
            <li key={ev.id} className="stat-proof__item">
              <button
                type="button"
                className="stat-proof__name"
                onClick={() => download(ev)}
                title="Download"
              >
                <Download size={12} /> {ev.file_name}
              </button>
              {ev.file_size_kb ? <span className="stat-proof__size">{ev.file_size_kb} KB</span> : null}
              {ev.attached_by_name && <span className="stat-proof__by">{ev.attached_by_name}</span>}
              {!readOnly && (
                <button
                  type="button"
                  className="stat-proof__del"
                  disabled={busy}
                  onClick={() => remove(ev.id)}
                  title="Remove"
                  data-testid={`BTN_statutory_evidence_${ev.id}_remove`}
                >
                  <Trash2 size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── Step row ──────────────────────────────────────────────────────────────────

// Flat table row — one obligation step per row (S.No · Step · Status · Action).
// Action controls (Mark Complete / Not Applicable) live inline; notes & skip
// reason are captured in a small popup instead of an inline expanding panel, so
// the modal reads as a clean checklist table (parity with audit ComplianceWalkthrough).
const StepRow: React.FC<{
  step: StatInstanceStep;
  idx: number;
  onComplete: (stepId: number, notes: string, complianceDate?: string, ackRef?: string) => Promise<void>;
  onSkip: (stepId: number, reason: string) => Promise<void>;
  onReload: () => Promise<void> | void;
  dueDate?: string | null;
  readOnly?: boolean;
}> = ({ step, idx, onComplete, onSkip, onReload, dueDate, readOnly = false }) => {
  const [mode, setMode] = useState<null | 'complete' | 'skip'>(null);
  const [notes, setNotes] = useState('');
  const [skipReason, setSkipReason] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // Compliance date defaults to today — the common case is recording something
  // done now — but stays editable so a late or back-recorded filing carries its
  // real date instead of the date someone got round to ticking the box.
  const [compDate, setCompDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [ackRef, setAckRef] = useState('');
  // Dates are rendered in the entity's configured format, same as the parent.
  const { formatDate } = useEntitySettings();
  const isActive = step.status === 'ACTIVE' && !readOnly;
  const evidence = step.evidence ?? [];
  const today = new Date().toISOString().slice(0, 10);
  // A discharge date after the due date is a late filing. Surfaced at entry
  // time, because that is the one fact the register exists to reveal.
  const isLate = !!(dueDate && compDate && compDate > dueDate.slice(0, 10));
  const close = () => { setMode(null); setErr(''); };

  return (
    <>
      <tr className="table-list-row">
        <td className="table-list-cell response-cell-sno">{idx + 1}</td>
        <td className="table-list-cell response-cell-question">
          <span className="table-list-cell-title">{step.step_name}</span>
          <span className="table-list-cell-sub">
            Step {step.step_number} · {step.authority_name} — <span className="italic">{ROLE_LABEL[step.role] ?? step.role}</span>
          </span>
          {step.status === 'COMPLETED' && (
            <span className="table-list-cell-sub stat-step-facts">
              {step.compliance_date && (
                <span className={isLate ? 'stat-fact stat-fact--late' : 'stat-fact'}>
                  Complied: {formatDate(step.compliance_date)}{isLate ? ' (late)' : ''}
                </span>
              )}
              {step.acknowledgement_ref && (
                <span className="stat-fact">Ack: {step.acknowledgement_ref}</span>
              )}
              <span className={evidence.length ? 'stat-fact' : 'stat-fact stat-fact--late'}>
                {evidence.length
                  ? `${evidence.length} proof file${evidence.length > 1 ? 's' : ''}`
                  : 'no proof'}
              </span>
            </span>
          )}
          {step.status === 'COMPLETED' && step.notes && (
            <span className="table-list-cell-sub" style={{ fontStyle: 'italic' }}>{step.notes}</span>
          )}
          {step.status === 'COMPLETED' && evidence.length > 0 && (
            <ProofPanel
              stepId={step.id}
              evidence={evidence}
              readOnly
              onChanged={onReload}
              onError={setErr}
            />
          )}
          {step.status === 'SKIPPED' && step.skip_reason && (
            <span className="table-list-cell-sub" style={{ fontStyle: 'italic', color: 'var(--amber-600, #d97706)' }}>
              Skipped: {step.skip_reason}
            </span>
          )}
        </td>
        <td className="table-list-cell">
          <span className={`status-badge sm ${statusVariant(step.status)}`}>{step.status}</span>
        </td>
        <td className="table-list-cell table-list-cell--actions">
          {isActive ? (
            <div className="response-action-cluster" style={{ flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
              <Button
                variant="fill"
                disabled={busy}
                data-testid={`BTN_statutory_step_${step.id}_complete`}
                onClick={() => { setNotes(''); setErr(''); setMode('complete'); }}
              >
                <CheckCircle size={13} className="mr-1" /> Mark Complete
              </Button>
              <Button
                variant="outline"
                disabled={busy}
                data-testid={`BTN_statutory_step_${step.id}_skip`}
                onClick={() => { setSkipReason(''); setErr(''); setMode('skip'); }}
              >
                <SkipForward size={13} className="mr-1" /> Not Applicable
              </Button>
            </div>
          ) : (
            <span className="text-gray-300">—</span>
          )}
        </td>
      </tr>

      {mode === 'complete' && (
        <Modal isOpen onClose={close} size="md">
          <Modal.Header onClose={close}>Mark Complete — {step.step_name}</Modal.Header>
          <Modal.Body>
            <div className="stat-complete-grid">
              <div>
                <label className="stat-field-label" htmlFor={`comp-date-${step.id}`}>
                  Date of compliance <span className="stat-proof__req">required</span>
                </label>
                <FormControl.Input
                  id={`comp-date-${step.id}`}
                  type="date"
                  max={today}
                  noMargin
                  data-testid={`INP_statutory_step_${step.id}_compliance_date`}
                  value={compDate}
                  onChange={(e) => { setErr(''); setCompDate(e.target.value); }}
                />
                <span className="stat-field-hint">
                  {dueDate ? `Due ${formatDate(dueDate)}. ` : ''}
                  When it was actually filed, not today's date.
                </span>
              </div>
              <div>
                <label className="stat-field-label" htmlFor={`ack-${step.id}`}>
                  Acknowledgement / challan no.
                </label>
                <FormControl.Input
                  id={`ack-${step.id}`}
                  type="text"
                  noMargin
                  maxLength={200}
                  placeholder="e.g. RTI/ABU/2026-27/0914"
                  data-testid={`INP_statutory_step_${step.id}_ack_ref`}
                  value={ackRef}
                  onChange={(e) => { setErr(''); setAckRef(e.target.value); }}
                />
                <span className="stat-field-hint">Issued by the authority, if any.</span>
              </div>
            </div>

            {isLate && (
              <div className="stat-late-note">
                This date is after the due date of {formatDate(dueDate)} — it will be
                recorded as a late compliance.
              </div>
            )}

            <ProofPanel
              stepId={step.id}
              evidence={evidence}
              onChanged={onReload}
              onError={setErr}
            />

            <label className="stat-field-label" htmlFor={`notes-${step.id}`}>Notes</label>
            <FormControl.Textarea
              id={`notes-${step.id}`}
              rows={3}
              noMargin
              placeholder="What was filed, and anything an auditor should know (optional)…"
              data-testid={`INP_statutory_step_${step.id}_notes`}
              value={notes}
              onChange={(e) => { setErr(''); setNotes(e.target.value); }}
              onBlur={(e) => {
                const v = e.target.value;
                if (!v || !v.trim()) { setErr(''); return; }
                const r = validate(110, v);
                setErr(r.valid ? '' : (r.message ?? 'Invalid notes.'));
              }}
            />
            {err && <div className="response-inline-warning">{err}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline" onClick={close} disabled={busy}>Cancel</Button>
            <Button
              variant="fill"
              disabled={busy || !!err || !compDate || evidence.length === 0}
              title={
                !compDate ? 'Enter the date of compliance'
                  : evidence.length === 0 ? 'Attach proof of compliance first'
                  : undefined
              }
              onClick={async () => {
                if (!compDate) { setErr('Enter the date the obligation was discharged.'); return; }
                if (compDate > today) { setErr('The date of compliance cannot be in the future.'); return; }
                if (evidence.length === 0) { setErr('Attach proof of compliance before marking complete.'); return; }
                if (notes.trim()) {
                  const r = validate(110, notes);
                  if (!r.valid) { setErr(r.message ?? 'Invalid notes.'); return; }
                }
                setBusy(true);
                try { await onComplete(step.id, notes, compDate, ackRef.trim() || undefined); close(); }
                catch (e: any) {
                  setErr(e?.response?.data?.detail || 'Could not mark this step complete.');
                }
                finally { setBusy(false); }
              }}
            >
              <CheckCircle size={13} className="mr-1" /> {busy ? 'Saving…' : 'Confirm'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {mode === 'skip' && (
        <Modal isOpen onClose={close} size="md">
          <Modal.Header onClose={close}>Not Applicable — {step.step_name}</Modal.Header>
          <Modal.Body>
            <FormControl.Textarea
              rows={4}
              noMargin
              placeholder="Reason this step is not applicable this period (required)…"
              data-testid={`INP_statutory_step_${step.id}_skip_reason`}
              value={skipReason}
              onChange={(e) => { setErr(''); setSkipReason(e.target.value); }}
              onBlur={(e) => {
                const v = e.target.value;
                if (!v || !v.trim()) { setErr(''); return; }
                const r = validate(110, v);
                setErr(r.valid ? '' : (r.message ?? 'Invalid reason.'));
              }}
              autoFocus
            />
            {err && <div className="response-inline-warning">{err}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline" onClick={close} disabled={busy}>Cancel</Button>
            <Button
              variant="fill"
              disabled={busy || !skipReason.trim() || !!err}
              onClick={async () => {
                const r = validate(110, skipReason.trim());
                if (!r.valid) { setErr(r.message ?? 'Invalid reason.'); return; }
                setBusy(true);
                try { await onSkip(step.id, skipReason.trim()); close(); }
                finally { setBusy(false); }
              }}
            >
              <SkipForward size={13} className="mr-1" /> {busy ? 'Skipping…' : 'Confirm Skip'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

// ── Instance detail modal ─────────────────────────────────────────────────────

const InstanceDetailModal: React.FC<{
  instanceId: number;
  onClose: () => void;
  onRefreshParent: () => void;
  readOnly?: boolean;
}> = ({ instanceId, onClose, onRefreshParent, readOnly = false }) => {
  const { formatDate } = useEntitySettings();
  const [detail, setDetail] = useState<StatInstanceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  // Checker (Approve/Review) actions captured from the footer — same two the
  // maker had (Mark Complete / Not Applicable), just relocated to the footer.
  const [checkerMode, setCheckerMode] = useState<null | 'complete' | 'skip'>(null);
  const [checkerNotes, setCheckerNotes] = useState('');
  const [checkerReason, setCheckerReason] = useState('');
  const [checkerErr, setCheckerErr] = useState('');
  const [checkerBusy, setCheckerBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const d = await statutoryService.getInstance(instanceId);
      setDetail(d);
    } catch {
      setToast({ type: 'error', message: 'Failed to load obligation details.' });
    }
  }, [instanceId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await reload();
      setLoading(false);
    })();
  }, [reload]);

  const handleComplete = async (
    stepId: number, notes: string, complianceDate?: string, ackRef?: string,
  ) => {
    await statutoryService.completeStep(stepId, notes, complianceDate, ackRef);
    await reload();
    onRefreshParent();
  };

  const handleSkip = async (stepId: number, reason: string) => {
    await statutoryService.skipStep(stepId, reason);
    await reload();
    onRefreshParent();
  };

  const progress = detail
    ? Math.round(((detail.completed_count ?? 0) / Math.max(detail.step_count ?? 1, 1)) * 100)
    : 0;

  // Maker checklist excludes checker (Approve / Review) steps — see isMakerStep.
  const makerSteps = (detail?.steps ?? []).filter(isMakerStep);

  // The checker action lives in the modal footer (not inline). It shows only
  // when the currently-active step is a checker (Approve / Review) step AND the
  // logged-in user is assigned to it (can_action from the backend). A maker
  // never sees it.
  const activeCheckerStep = (detail?.steps ?? []).find(
    (s) => s.status === 'ACTIVE' && !isMakerStep(s) && s.can_action && !readOnly,
  ) ?? null;

  return (
    <>
      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
      <Modal isOpen onClose={onClose} size="xl">
        <Modal.Header onClose={onClose}>
        Statutory Obligation - {detail?.filing_name ?? 'Loading…'}
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-400 animate-pulse">Loading…</div>
          ) : detail ? (
            <div className="space-y-4">
              {/* Meta chips + status + progress on ONE line — no separate row /
                  wasted divider. Progress bar fills the remaining width. */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                {detail.obligation_type && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                    <Scale size={12} className="text-amber-500" />
                    <span className="truncate">{detail.obligation_type}</span>
                  </div>
                )}
                {detail.location_name && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                    <MapPin size={12} className="text-blue-400" />
                    <span>{detail.location_name}</span>
                  </div>
                )}
                {detail.period_label && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                    <Calendar size={12} className="text-purple-400" />
                    <span>{detail.period_label}</span>
                  </div>
                )}
                {detail.due_date && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                    <Calendar size={12} className="text-amber-500" />
                    <span>Due: {formatDate(detail.due_date)}</span>
                  </div>
                )}
                <span className={`status-badge sm ${statusVariant(detail.status)}`}>
                  {detail.status.replace('_', ' ')}
                </span>
                <div className="step-progress-bar ml-auto">
                  <div className="step-progress-bar__track">
                    <div
                      className={`step-progress-bar__fill ${detail.status === 'COMPLETED' ? 'bg-green-400' : 'bg-amber-400'}`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="step-progress-bar__text">
                    {detail.completed_count}/{detail.step_count} steps
                  </span>
                </div>
              </div>

              {/* Steps — flat checklist table (S.No · Step · Status · Action) */}
              <div className="table-list response-qtable">
                <div className="table-list-scroll">
                  <table className="table-list-table">
                    <colgroup>
                      <col className="response-col-sno" />
                      <col />
                      <col className="response-col-answer" />
                      <col />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="table-list-th response-th-sno">S.No</th>
                        <th className="table-list-th">Obligation Step</th>
                        <th className="table-list-th">Status</th>
                        <th className="table-list-th table-list-th--right">Action</th>
                      </tr>
                    </thead>
                    <tbody aria-live="polite">
                      {detail.status === 'PENDING' ? (
                        <tr>
                          <td colSpan={4} className="table-list-cell">
                            <div className="stat-not-started">
                              <Clock size={15} />
                              <div>
                                <strong>This period has not started yet.</strong>
                                <span>
                                  It becomes actionable closer to its due date of{' '}
                                  {formatDate(detail.due_date)}. Nothing to record here today —
                                  open a period marked <em>In Progress</em> to file and sign off.
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : makerSteps.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="table-list-cell">
                            <NoDataFound title="No Steps" message="No steps defined for this obligation." />
                          </td>
                        </tr>
                      ) : (
                        makerSteps.map((step, i) => (
                          <StepRow
                            key={step.id}
                            step={step}
                            idx={i}
                            onComplete={handleComplete}
                            onSkip={handleSkip}
                            onReload={reload}
                            dueDate={detail.due_date}
                            readOnly={readOnly}
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </Modal.Body>

        {/* Checker actions — the same two the maker had (Mark Complete / Not
            Applicable), shown on the footer's right only to the checker assigned
            to the active Approve/Review step. Makers never see them. */}
        {activeCheckerStep && (
          <Modal.Footer>
            <Button
              variant="fill"
              disabled={checkerBusy}
              data-testid={`BTN_statutory_checker_${activeCheckerStep.id}_complete`}
              onClick={() => { setCheckerNotes(''); setCheckerErr(''); setCheckerMode('complete'); }}
            >
              <CheckCircle size={13} className="mr-1" /> Mark Complete
            </Button>
            <Button
              variant="outline"
              disabled={checkerBusy}
              data-testid={`BTN_statutory_checker_${activeCheckerStep.id}_skip`}
              onClick={() => { setCheckerReason(''); setCheckerErr(''); setCheckerMode('skip'); }}
            >
              <SkipForward size={13} className="mr-1" /> Not Applicable
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Checker — Mark Complete popup (notes optional) → /complete */}
      {activeCheckerStep && checkerMode === 'complete' && (
        <Modal isOpen onClose={() => { setCheckerMode(null); setCheckerErr(''); }} size="md">
          <Modal.Header onClose={() => { setCheckerMode(null); setCheckerErr(''); }}>
            Mark Complete — {activeCheckerStep.step_name}
          </Modal.Header>
          <Modal.Body>
            {/* What the maker recorded. An approver signing off without the
                date and the filed document in front of them is not reviewing
                anything, so it is shown here rather than left a click away. */}
            {(() => {
              const performed = (detail?.steps ?? []).find(
                (st) => isMakerStep(st) && st.status === 'COMPLETED',
              );
              if (!performed) return null;
              const late = !!(detail?.due_date && performed.compliance_date
                && performed.compliance_date.slice(0, 10) > detail.due_date.slice(0, 10));
              return (
                <div className="stat-review-box">
                  <span className="stat-field-label">Submitted for approval</span>
                  <div className="stat-step-facts">
                    {performed.compliance_date && (
                      <span className={late ? 'stat-fact stat-fact--late' : 'stat-fact'}>
                        Complied: {formatDate(performed.compliance_date)}{late ? ' (late)' : ''}
                      </span>
                    )}
                    {performed.acknowledgement_ref && (
                      <span className="stat-fact">Ack: {performed.acknowledgement_ref}</span>
                    )}
                  </div>
                  {performed.notes && <p className="stat-review-notes">{performed.notes}</p>}
                  <ProofPanel
                    stepId={performed.id}
                    evidence={performed.evidence ?? []}
                    readOnly
                    onChanged={reload}
                    onError={setCheckerErr}
                  />
                </div>
              );
            })()}
            <label className="stat-field-label" htmlFor="checker-notes">Approval remarks</label>
            <FormControl.Textarea
              id="checker-notes"
              rows={3}
              noMargin
              placeholder="Approval remarks (optional)…"
              data-testid={`INP_statutory_checker_${activeCheckerStep.id}_notes`}
              value={checkerNotes}
              onChange={(e) => { setCheckerErr(''); setCheckerNotes(e.target.value); }}
              onBlur={(e) => {
                const v = e.target.value;
                if (!v || !v.trim()) { setCheckerErr(''); return; }
                const r = validate(110, v);
                setCheckerErr(r.valid ? '' : (r.message ?? 'Invalid notes.'));
              }}
              autoFocus
            />
            {checkerErr && <div className="response-inline-warning">{checkerErr}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline" onClick={() => { setCheckerMode(null); setCheckerErr(''); }} disabled={checkerBusy}>Cancel</Button>
            <Button
              variant="fill"
              disabled={checkerBusy || !!checkerErr}
              onClick={async () => {
                if (checkerNotes.trim()) {
                  const r = validate(110, checkerNotes);
                  if (!r.valid) { setCheckerErr(r.message ?? 'Invalid notes.'); return; }
                }
                setCheckerBusy(true);
                try {
                  await handleComplete(activeCheckerStep.id, checkerNotes);
                  setCheckerMode(null);
                } finally { setCheckerBusy(false); }
              }}
            >
              <CheckCircle size={13} className="mr-1" /> {checkerBusy ? 'Saving…' : 'Confirm'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Checker — Not Applicable popup (reason required) → /skip */}
      {activeCheckerStep && checkerMode === 'skip' && (
        <Modal isOpen onClose={() => { setCheckerMode(null); setCheckerErr(''); }} size="md">
          <Modal.Header onClose={() => { setCheckerMode(null); setCheckerErr(''); }}>
            Not Applicable — {activeCheckerStep.step_name}
          </Modal.Header>
          <Modal.Body>
            <FormControl.Textarea
              rows={4}
              noMargin
              placeholder="Reason this step is not applicable this period (required)…"
              data-testid={`INP_statutory_checker_${activeCheckerStep.id}_skip_reason`}
              value={checkerReason}
              onChange={(e) => { setCheckerErr(''); setCheckerReason(e.target.value); }}
              onBlur={(e) => {
                const v = e.target.value;
                if (!v || !v.trim()) { setCheckerErr(''); return; }
                const r = validate(110, v);
                setCheckerErr(r.valid ? '' : (r.message ?? 'Invalid reason.'));
              }}
              autoFocus
            />
            {checkerErr && <div className="response-inline-warning">{checkerErr}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline" onClick={() => { setCheckerMode(null); setCheckerErr(''); }} disabled={checkerBusy}>Cancel</Button>
            <Button
              variant="fill"
              disabled={checkerBusy || !checkerReason.trim() || !!checkerErr}
              onClick={async () => {
                const r = validate(110, checkerReason.trim());
                if (!r.valid) { setCheckerErr(r.message ?? 'Invalid reason.'); return; }
                setCheckerBusy(true);
                try {
                  await handleSkip(activeCheckerStep.id, checkerReason.trim());
                  setCheckerMode(null);
                } finally { setCheckerBusy(false); }
              }}
            >
              <SkipForward size={13} className="mr-1" /> {checkerBusy ? 'Skipping…' : 'Confirm Skip'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

// ── Main tab component ────────────────────────────────────────────────────────

interface StatutoryTabProps {
  scope?: 'active' | 'archive';
  readOnly?: boolean;
}

const StatutoryTab: React.FC<StatutoryTabProps> = ({ scope = 'active', readOnly = false }) => {
  const { formatDate } = useEntitySettings();
  const [search, setSearch] = useState('');
  const [instances, setInstances] = useState<StatInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [openInstanceId, setOpenInstanceId] = useState<number | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadInstances = useCallback(async () => {
    setLoading(true);
    try {
      const data = await statutoryService.listInstances('my_tasks');
      setInstances(data);
    } catch {
      setToast({ type: 'error', message: 'Failed to load statutory obligations.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInstances();
  }, [loadInstances]);

  // Reset to first page whenever the search or page-size changes.
  useEffect(() => { setPage(1); }, [search, pageSize, scope]);

  // History (archive) keeps only closed/completed obligations; Action Centre is unchanged.
  const scoped = visibleForScope(instances, (i) => isClosedInstance(i.status), scope);
  const filtered = scoped.filter((i) =>
    (i.filing_name ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (i.obligation_type ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (i.period_label ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (i.location_name ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const totalRecords = filtered.length;
  const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}

      {openInstanceId != null && (
        <InstanceDetailModal
          instanceId={openInstanceId}
          onClose={() => setOpenInstanceId(null)}
          onRefreshParent={loadInstances}
          readOnly={readOnly}
        />
      )}

      <div className="flex h-full overflow-hidden">

        {/* ── Obligation list: toolbar + table ── */}
        <div className="netbody-card side-content-card flex flex-col flex-1 min-w-0">

          {/* Toolbar */}
          <div className="netbody-header">
            <div className="flex items-center gap-2 ml-auto">
              <FormControl.Input
                type="text"
                placeholder="Search obligations…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leadingIcon={<Search size={14} />}
                className="filter-search"
                data-testid="INP_statutory_search"
              />
              <Button
                variant="secondary-outline"
                icon={RefreshCw}
                onClick={loadInstances}
                data-testid="BTN_statutory_refresh"
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Body */}
          <div className="netbody-content">
            <div className="scroll-container flex-1 overflow-hidden">
              <div className="table-list">
                <div className="table-list-scroll">
                  <table className="table-list-table">
                    <thead>
                      <tr>
                        <th className="table-list-th table-list-cell--sno">S.No</th>
                        <th className="table-list-th">Obligation</th>
                        <th className="table-list-th table-list-cell--status">Status</th>
                        <th className="table-list-th">Progress</th>
                        <th className="table-list-th">Due</th>
                      </tr>
                    </thead>
                    <tbody aria-busy={loading} aria-live="polite">
                      {loading && <Skeleton.TableRows cols={['3xs', 'lg', 'pill', 'md', 'sm']} />}

                      {!loading && filtered.length === 0 && (
                        <tr>
                          <td colSpan={5} className="table-list-cell">
                            <NoDataFound
                              title={
                                scope === 'archive'
                                  ? 'No Closed Obligations'
                                  : 'No Statutory Obligations'
                              }
                              message={
                                scope === 'archive'
                                  ? 'No closed statutory obligations.'
                                  : 'No statutory obligations assigned to you. Obligations appear here once invoked by the GRC team.'
                              }
                            />
                          </td>
                        </tr>
                      )}

                      {!loading && paginatedRows.map((inst, idx) => {
                        const progress = Math.round(
                          ((inst.completed_count ?? 0) / Math.max(inst.step_count ?? 1, 1)) * 100
                        );
                        return (
                          <tr
                            key={inst.id}
                            className="table-list-row"
                            onClick={() => setOpenInstanceId(inst.id)}
                            data-testid={`BTN_statutory_row_${inst.id}_open`}
                          >
                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                            <td className="table-list-cell">
                              <div className="flex items-center gap-2">
                                <span className="flex-shrink-0">{STATUS_ICON[inst.status]}</span>
                                <div className="min-w-0">
                                  <span className="table-list-cell-title">{inst.filing_name}</span>
                                  <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-gray-400 mt-0.5">
                                    {inst.obligation_type && <span>{inst.obligation_type}</span>}
                                    {inst.period_label && <span>{inst.period_label}</span>}
                                    {inst.location_name && <span>{inst.location_name}</span>}
                                    {inst.initiated_by_name && <span>by {inst.initiated_by_name}</span>}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="table-list-cell">
                              <span className={`status-badge xs ${statusVariant(inst.status)}`}>
                                {inst.status.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="table-list-cell">
                              <div className="step-progress-bar">
                                <div className="step-progress-bar__track">
                                  <div
                                    className={`step-progress-bar__fill ${inst.status === 'COMPLETED' ? 'bg-green-400' : 'bg-amber-400'}`}
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                                <span className="step-progress-bar__text">
                                  {inst.completed_count}/{inst.step_count}
                                </span>
                              </div>
                            </td>
                            <td className="table-list-cell">
                              {inst.due_date
                                ? <span className="text-amber-500 font-medium text-xs">{formatDate(inst.due_date)}</span>
                                : <span className="text-gray-400">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                  pageSizeOptions={[10, 25, 50, 100]}
                  recordLabel="obligations"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StatutoryTab;
